<template>
  <div>
    <div class="info-area">
      <div class="area-title-wrapper">
        <h3 class="area-title">安全员信息</h3>
        <a-button type="primary" @click="openSelectModal">选择安全员</a-button>
      </div>

      <div class="security-users-list">
        <div v-for="(user, index) in localUsers" :key="index" class="user-card">
          <div class="user-header">
            <span class="user-index">安全员 {{ index + 1 }}</span>
            <a-button v-if="localUsers.length > 1" type="text" danger @click="handleRemoveUser(index)">删除</a-button>
          </div>

          <div class="personnel-form">
            <div class="form-row">
              <div class="form-item">
                <label class="form-label">姓名 <span class="required">*</span></label>
                <div class="form-input-wrapper">
                  <a-input v-model:value="user.name" placeholder="请输入姓名" class="form-input" :disabled="user.isSelected" />
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-item">
                <label class="form-label">性别 <span class="required">*</span></label>
                <div class="form-input-wrapper">
                  <a-select v-model:value="user.gender" placeholder="请选择性别" class="form-input" :disabled="user.isSelected">
                    <a-select-option value="1">男</a-select-option>
                    <a-select-option value="0">女</a-select-option>
                  </a-select>
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-item">
                <label class="form-label">联系方式 <span class="required">*</span></label>
                <div class="form-input-wrapper">
                  <a-input v-model:value="user.phone" placeholder="请输入联系方式" class="form-input" :disabled="user.isSelected" />
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-item">
                <label class="form-label">身份证号码 <span class="required">*</span></label>
                <div class="form-input-wrapper">
                  <a-input v-model:value="user.idCard" placeholder="请输入身份证号码" class="form-input" :disabled="user.isSelected" />
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-item">
                <label class="form-label">驾驶证号</label>
                <div class="form-input-wrapper">
                  <a-input v-model:value="user.licenseNumber" placeholder="请输入驾驶证号" class="form-input" :disabled="user.isSelected" />
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-item">
                <label class="form-label">培训日期</label>
                <div class="form-input-wrapper">
                  <a-date-picker v-model:value="user.trainingDate" placeholder="请选择培训日期" format="YYYY-MM-DD" value-format="YYYY-MM-DD" class="form-input" :disabled="user.isSelected" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <a-button type="dashed" @click="handleAddUser" style="width: 100%; margin: 10px 0; padding: 8px 5px; font-size: 16px; text-align: center; line-height: 24px;">
          + 添加安全员
        </a-button>
      </div>
    </div>

    <a-modal
      v-model:open="selectModalVisible"
      title="选择安全员"
      width="1170px"
      @cancel="closeSelectModal"
    >
      <div class="select-modal-content">
        <div v-if="securityUserList.length === 0" class="no-data">
          <p>暂无可用的安全员信息</p>
        </div>
        <div v-else class="security-user-table">
          <div class="table-header">
            <div class="table-header-item">姓名</div>
            <div class="table-header-item">性别</div>
            <div class="table-header-item">联系方式</div>
            <div class="table-header-item">身份证号</div>
            <div class="table-header-item">驾驶证号</div>
            <div class="table-header-item">培训日期</div>
            <div class="table-header-item">操作</div>
          </div>
          <div class="table-body">
            <div
              v-for="user in securityUserList"
              :key="user.id"
              class="table-row"
            >
              <div class="table-cell">{{ user.name || '-' }}</div>
              <div class="table-cell">{{ user.gender === 1 ? '男' : user.gender === 0 ? '女' : '-' }}</div>
              <div class="table-cell">{{ user.phone || '-' }}</div>
              <div class="table-cell">{{ user.idCard || '-' }}</div>
              <div class="table-cell">{{ user.licenseNumber || '-' }}</div>
              <div class="table-cell">{{ user.trainingDate || '-' }}</div>
              <div class="table-cell">
                <a-button type="primary" size="small" @click="handleSelectUser(user)">选择</a-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <a-button @click="closeSelectModal">取消</a-button>
      </template>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { getSecurityUserPageList } from '@/api/securityUser/securityUser';

const props = defineProps({
  securityUsers: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:security-users']);

const localUsers = ref([]);
const selectModalVisible = ref(false);
const securityUserList = ref([]);

const createEmptyUser = () => ({
  id: null,
  name: '',
  gender: '',
  phone: '',
  idCard: '',
  licenseNumber: '',
  trainingDate: '',
  isSelected: false
});

const syncFromProps = () => {
  if (Array.isArray(props.securityUsers) && props.securityUsers.length > 0) {
    localUsers.value = props.securityUsers.map(user => ({ ...user }));
  } else if (!localUsers.value.length) {
    localUsers.value = [createEmptyUser()];
  }
};

const notifyParent = () => {
  emit('update:security-users', JSON.parse(JSON.stringify(localUsers.value)));
};

onMounted(() => {
  syncFromProps();
});

const handleAddUser = () => {
  localUsers.value.push(createEmptyUser());
  notifyParent();
};

const handleRemoveUser = (index) => {
  localUsers.value.splice(index, 1);
  notifyParent();
};

const openSelectModal = () => {
  fetchSecurityUserList();
  selectModalVisible.value = true;
};

const closeSelectModal = () => {
  selectModalVisible.value = false;
};

const fetchSecurityUserList = async () => {
  try {
    const response = await getSecurityUserPageList({
      pageIndex: 1,
      pageSize: 100
    });
    if (response.code === 200 && response.data) {
      securityUserList.value = response.data.records;
    }
  } catch (error) {
    console.error('获取安全员列表失败:', error);
  }
};

const handleSelectUser = (user) => {
  localUsers.value.push({
    id: user.id || null,
    name: user.name || '',
    gender: user.gender !== undefined ? user.gender.toString() : '',
    phone: user.phone || '',
    idCard: user.idCard || '',
    licenseNumber: user.licenseNumber || '',
    trainingDate: user.trainingDate || '',
    isSelected: true
  });
  selectModalVisible.value = false;
  notifyParent();
};
</script>

<style scoped lang="less">
.info-area {
  margin-bottom: 20px;
  padding: 20px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  background-color: #ffffff;
}

.area-title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #d9d9d9;
}

.area-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.security-users-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.user-card {
  padding: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
}

.user-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #d9d9d9;
}

.user-index {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.personnel-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: flex;
  gap: 24px;
  justify-content: center;
}

.form-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 400px;
  width: 100%;
  max-width: 500px;
}

.form-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
  text-align: right;
  flex-shrink: 0;
  width: 100px;

  .required {
    color: #f5222d;
    margin-left: 4px;
  }
}

.form-input-wrapper {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
  width: 350px;
  justify-content: flex-start;
}

.form-input {
  width: 100%;
}

:deep(.ant-input),
:deep(.ant-select-selector),
:deep(.ant-picker) {
  width: 100%;
  text-align: left;
}

.select-modal-content {
  max-height: 400px;
  overflow-y: auto;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #999;
}

.security-user-table {
  width: 100%;
}

.table-header {
  display: flex;
  background-color: #fafafa;
  border-bottom: 1px solid #e8e8e8;
}

.table-header-item {
  flex: 1;
  padding: 12px;
  text-align: center;
  font-weight: 500;
  color: #666;
  font-size: 14px;
}

.table-body {
  border-bottom: 1px solid #e8e8e8;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;

  &:hover {
    background-color: #fafafa;
  }
}

.table-cell {
  flex: 1;
  padding: 12px;
  text-align: center;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>